import {
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import { useCatalogEvent } from 'lib/analytics/catalog'
import {
  ASK_FREE_TEXT,
  ASK_TITLE_MAX,
  ASK_TITLE_MIN,
  askBody,
  askButtonLabel,
  askChips,
  askClaimedLine,
  askedChipText,
  askSentLine,
  askTemplateFor,
  askTitle,
  askTitleReady,
} from 'lib/community/ask-rules'
import { discussionsApi } from 'lib/community/discussions-api'
import { DISCUSSIONS_PATH, rootHref } from 'lib/community/discussions'
import type {
  DiscussionCreateBody,
  DiscussionCreateResponse,
} from 'lib/community/discussions-types'
import { isOwnReview } from 'lib/community/helpful'
import { authorShownName } from 'lib/community/model'
import {
  FEED_CONTROL_ANALYTICS,
  type CommunityFeedItem,
  type FeedControlAnalytics,
} from 'lib/community/types'
import { withSlash } from 'lib/dealhunt/url'
import { authStateParam } from 'lib/owner-notes/analytics'
import { OwnerApiError, sharedMe } from 'lib/owner-notes/api'
import {
  useOwnerCredentialOptional,
  useOwnerSessionOptional,
  type OwnerCredential,
  type OwnerLogInOptions,
} from 'lib/owner-notes/auth-bridge'
import { hasDeviceMarker, useDeviceEpoch } from 'lib/owner-notes/declared'
import { sendAsDevice } from 'lib/owner-notes/device'
import { markOfferShown, useClaimAfterSignIn } from 'lib/owner-notes/claim-after-sign-in'
import { TurnstileError, turnstileConfigured, useTurnstile } from 'lib/owner-notes/turnstile'
import { MemberViewContext } from './useOwnMemberMe'
import styles from './AskOwner.module.css'

/** The open state's focus runs in the click's own task (iOS raises the keyboard only then). */
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

/**
 * The Ask just opened: its "Your question…" field takes the focus (the keyboard comes up on a
 * phone) and is brought into view (Guillaume 2026-10-18: "pills at the top and field below" — the
 * reader can type at once). The scroll is ours (`nearest`, smooth) so focus does not jump the page.
 * Returns whether a field was there to focus.
 */
export function focusAskField(
  field: Pick<HTMLInputElement, 'focus' | 'scrollIntoView'> | null
): boolean {
  if (!field) {
    return false
  }
  field.focus({ preventScroll: true })
  field.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  return true
}

/**
 * A tap on a suggestion chip (UX review 2026-10-18 — a tap used to post at once, no confirm or
 * undo): its text replaces the field's and the field takes the focus, in the tap's own task (iOS
 * raises the keyboard only then — Constraint 50e2bd5c). Nothing is posted; "Ask" posts.
 */
export function fillAskFromChip(
  chipText: string,
  setText: (text: string) => void,
  field: Pick<HTMLInputElement, 'focus' | 'scrollIntoView'> | null
): void {
  setText(chipText)
  focusAskField(field)
}

/** A typed question below DealHunt's minimum: said, not a silently dead button. */
const TOO_SHORT_LINE = `A question needs at least ${ASK_TITLE_MIN} characters.`

// ── one open per page ────────────────────────────────────────────────────────────────────────

let openNote: string | null = null
const openListeners = new Set<() => void>()

function setOpenNote(noteId: string | null): void {
  openNote = noteId
  openListeners.forEach(listener => listener())
}

function subscribeOpen(listener: () => void): () => void {
  openListeners.add(listener)
  return () => {
    openListeners.delete(listener)
  }
}

// ── what this browser asked since the page loaded (client-only, never in the HTML) ────────────

/** note id → chip id → the new question's page: a remounted row keeps its "· asked" chips. */
const chipsAskedHere = new Map<string, Map<string, string>>()
/** note id → typed questions posted here, oldest first. Never deduplicated. */
const freeAskedHere = new Map<string, Array<{ text: string; url: string }>>()

/** Signed-out errors that mean "this needs a member": the sign-in, with the Ask's reason. */
const SIGN_IN_CODES: ReadonlySet<string> = new Set([
  'auth_required',
  'auth_invalid',
  'identity_required',
])

const FALLBACK_LINE = "We couldn't send your question — try again in a moment."

/** The claim after the Ask's sign-in failed in a way a later sign-in may fix. */
const CLAIM_FAILED_LINE =
  "You're signed in, but we couldn't link your question to your account yet — it is still posted."

/** The sign-in offered AFTER a question posted (feed plan §2: "Get the answer by email"). */
const ASK_LOGIN: OwnerLogInOptions = {
  copy: {
    title: 'Get the answer by email',
    subtitle: "Sign in and we'll email you when an owner answers your question.",
  },
}

interface AskOwnerParts {
  /** "Ask <handle>" — the vote line's right end. */
  button: React.ReactNode
  /** The chips and "Your question…" under the line; closed (hidden) in the server HTML. */
  panel: React.ReactNode
}

interface AskOwnerProps {
  item: CommunityFeedItem
  panelClassName?: string
  /** The row places the two parts: the button in the vote line, the panel under it. */
  children: (parts: AskOwnerParts) => React.ReactNode
  /** The Ask events' `page_type` / `location` — the feed's by default (a best list's Owners
   *  block passes its own through `FeedRow`). */
  analytics?: FeedControlAnalytics
}

/** The author's own review, known only after hydration (VoteControl's rule, `isOwnReview`). */
function useOwnReview(noteId: string, handle: string): boolean {
  const epoch = useDeviceEpoch()
  const [own, setOwn] = useState(false)
  useEffect(() => {
    if (!hasDeviceMarker()) {
      return undefined
    }
    let live = true
    sharedMe().then(
      me => {
        if (live) {
          setOwn(isOwnReview(me, { noteId, handle }))
        }
      },
      () => undefined
    )
    return () => {
      live = false
    }
  }, [epoch, handle, noteId])
  return own
}

/**
 * "Ask <handle>" on a community feed row (feed plan §2 "Ask", slice 4; DealHunt contract #205,
 * #206 and the device-author follow-up): one button at the right end of the vote line; a tap
 * opens the review's chips inline under the line — no sign-in — and, below them, the
 * "Your question…" field, focused the moment the panel opens (`focusAskField`; the chips stay
 * above it as suggestions). A tap on a chip puts its text in the field and focuses it
 * (`fillAskFromChip`), the chip shown selected; "Ask" posts the field as a question on the
 * product (`askBody`: title-only; the chip id as `template_id` and the review as `note_id` only
 * while the text is the chip's unedited text, `askTemplateFor` — edited text is a typed
 * question). After a chip's post it turns into its dashed "· asked" link; a signed-out reader is
 * offered the sign-in with its reason ("Get the answer by email"), a signed-in one reads
 * `askSentLine` (who is notified, and the email promise).
 *
 * Shared cache: the server HTML carries the button and the CLOSED panel with the chips the item
 * itself decides (no field: it renders only once the reader opens the panel, never autofocused in
 * the HTML) (rules in `lib/community/ask-rules.ts`, the `asked` links included — they are
 * public). The open state, what this browser asked, the sign-in offer and the own-review state
 * are client-only. One panel open per page. Inert on the author's own review, as the vote.
 *
 * Who posts: a signed-in reader as the member; a signed-out reader with the owner-notes device,
 * minted on a first ask through the page's one mint (`sendAsDevice` → `mintOnce`, shared with
 * the vote).
 */
const AskOwner: React.VFC<AskOwnerProps> = ({
  item,
  panelClassName,
  children,
  analytics = FEED_CONTROL_ANALYTICS,
}) => {
  const { note, author, product } = item
  const noteId = note.id
  const handle = author.handle
  // The prose names the author as the byline does: the shown name, else the handle.
  const shown = authorShownName(author)
  const trailingSlash = publicConfig('useTrailingSlash')
  const session = useOwnerSessionOptional()
  const credential = useOwnerCredentialOptional()
  const { asVisitor } = useContext(MemberViewContext)
  const own = useOwnReview(noteId, handle)
  const turnstile = useTurnstile()
  const { track } = useCatalogEvent()
  const panelId = useId()
  const open = useSyncExternalStore(
    subscribeOpen,
    () => openNote === noteId,
    () => false
  )
  // The asks of this browser are read after mount only: the first client render matches the HTML.
  const [mounted, setMounted] = useState(false)
  const [, setAskedTick] = useState(0)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<'signed_in' | 'signed_out' | 'claimed' | null>(null)
  const [text, setText] = useState('')
  const busyRef = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => setMounted(true), [])

  // The sign-in the reader took from the toast landed (a NEW token) — on this page, or on the next
  // one after an email link or a reload: claim the device, so the question it asked becomes the
  // member's and its answer is emailed (contract #209). SG's sign-in never claims a device by
  // itself (#81); this claim follows the reader's own click on the Ask's offer
  // (`lib/owner-notes/claim-after-sign-in`, shared with the vote's toast).
  const claim = useClaimAfterSignIn(credential, outcome => {
    if (outcome.kind === 'linked') {
      setDone('claimed')
      return
    }
    setDone(null)
    // A conflict, or a lead no retry fixes (unverified email, Google required): DealHunt's /
    // the band's sentence. A retryable failure: the band's leads speak of publishing a review.
    if (outcome.kind === 'conflict') {
      setError(outcome.message)
    } else {
      setError(outcome.retry === 'none' ? outcome.lead : CLAIM_FAILED_LINE)
    }
  })
  const claimArmed = claim.armed

  // Signed in elsewhere since the offer showed (not through it): it has said what it had to say.
  const signedIn = session.resolved && session.signedIn
  useEffect(() => {
    if (signedIn && !claimArmed) {
      setDone(d => (d === 'signed_out' ? null : d))
    }
  }, [signedIn, claimArmed])

  const offerSignIn = (): void => {
    claim.arm()
    void session.showLogIn(ASK_LOGIN)
  }

  // Opened by the reader (the server snapshot is always closed): type at once.
  useIsoLayoutEffect(() => {
    if (open) {
      focusAskField(inputRef.current)
    }
  }, [open])

  const mineChips = mounted ? chipsAskedHere.get(noteId) : undefined
  const mineFree = (mounted && freeAskedHere.get(noteId)) || []
  const chips = askChips(item, trailingSlash, mineChips)
  // The chip whose unedited text the field holds: selected, and its id rides the post.
  const selectedChip = askTemplateFor(text, chips)
  // The member hub's "View as visitor": the author's row looks like a visitor's, a tap stays inert.
  const inert = own

  const toggle = (): void => {
    if (inert) {
      return
    }
    if (open) {
      setOpenNote(null)
      return
    }
    setOpenNote(noteId)
    track('community_page_owner_ask_open', {
      page_type: analytics.page_type,
      location: analytics.location,
      item_count: chips.filter(chip => !chip.asked).length,
      auth_state: authStateParam(session),
      target_product_id: product.node_id,
      target_product_name: product.name,
    })
  }

  const send = async (
    cred: OwnerCredential | null,
    body: DiscussionCreateBody
  ): Promise<DiscussionCreateResponse> => {
    if (cred) {
      return discussionsApi.ask(cred, body)
    }
    return sendAsDevice(turnstile.getToken, () => discussionsApi.ask(null, body))
  }

  const post = async (title: string, templateId: string | null): Promise<void> => {
    // Before the session check answers, a signed-in reader would ask as the device.
    if (!session.resolved || inert || busyRef.current) {
      return
    }
    const cred = credential
    // Production DealHunt refuses a device minted without a bot check: without a site key a
    // signed-out reader with no device can only ask signed in.
    if (!cred && !hasDeviceMarker() && !turnstileConfigured()) {
      void session.showLogIn()
      return
    }
    busyRef.current = true
    setBusy(templateId ?? 'free')
    setError(null)
    try {
      const answer = await send(cred, askBody(item, title, templateId))
      const url = withSlash(rootHref(answer?.url) ?? DISCUSSIONS_PATH, trailingSlash)
      if (templateId) {
        const map = chipsAskedHere.get(noteId) ?? new Map<string, string>()
        map.set(templateId, url)
        chipsAskedHere.set(noteId, map)
      } else {
        freeAskedHere.set(noteId, [
          ...(freeAskedHere.get(noteId) ?? []),
          { text: askTitle(title), url },
        ])
      }
      setText('')
      setAskedTick(n => n + 1)
      track('community_page_owner_ask_post', {
        page_type: analytics.page_type,
        location: analytics.location,
        action: templateId ? 'chip' : 'free',
        // The chip's bank id only — typed words never reach analytics.
        ...(templateId ? { label: templateId } : {}),
        auth_state: authStateParam(session),
        target_product_id: product.node_id,
        target_product_name: product.name,
      })
      if (!cred) {
        markOfferShown()
      }
      setDone(cred ? 'signed_in' : 'signed_out')
    } catch (e) {
      const code = e instanceof OwnerApiError ? e.code : ''
      if (SIGN_IN_CODES.has(code)) {
        // Nothing was posted: the plain sign-in, and the reader taps again once signed in.
        void session.showLogIn()
      } else if (e instanceof TurnstileError) {
        setError(e.message)
      } else {
        // A 429 cap, a `template_invalid` (a bank id DealHunt refuses: a bug, shown as such), any
        // validation line: DealHunt's words.
        setError(e instanceof OwnerApiError && e.message ? e.message : FALLBACK_LINE)
      }
    } finally {
      busyRef.current = false
      setBusy(null)
    }
  }

  const submitFree = (event: React.FormEvent): void => {
    event.preventDefault()
    if (askTitleReady(text)) {
      void post(text, selectedChip)
    } else {
      setError(TOO_SHORT_LINE)
    }
  }

  const button = (
    <button
      type="button"
      className={clsx(styles.askBtn, { [styles.askOpen]: open, [styles.own]: own && !asVisitor })}
      aria-expanded={open}
      aria-controls={panelId}
      aria-disabled={inert || undefined}
      title={inert && !asVisitor ? 'Your own review' : undefined}
      onClick={toggle}
    >
      {askButtonLabel(shown)}
    </button>
  )

  const panel = (
    <div
      id={panelId}
      className={clsx(styles.panel, panelClassName)}
      hidden={!open}
      aria-busy={busy !== null || undefined}
    >
      {(chips.length > 0 || mineFree.length > 0) && (
        <ul className={styles.chips}>
          {chips.map(chip => (
            <li key={chip.id}>
              {chip.asked ? (
                <Linked href={chip.asked.url} className={clsx(styles.chip, styles.done)}>
                  {askedChipText(chip.text, chip.asked.answered)}
                </Linked>
              ) : (
                <button
                  type="button"
                  className={clsx(styles.chip, { [styles.selected]: chip.id === selectedChip })}
                  aria-pressed={chip.id === selectedChip || undefined}
                  aria-disabled={busy !== null || undefined}
                  onClick={() => {
                    if (inert || busyRef.current) {
                      return
                    }
                    setError(null)
                    fillAskFromChip(chip.text, setText, inputRef.current)
                  }}
                >
                  {chip.text}
                </button>
              )}
            </li>
          ))}
          {mineFree.map(q => (
            <li key={q.url}>
              <Linked href={q.url} className={clsx(styles.chip, styles.done)}>
                {askedChipText(q.text, false)}
              </Linked>
            </li>
          ))}
        </ul>
      )}
      {/* The field below the chips — only once opened (the HTML stays closed and field-free). */}
      {open && (
        <form className={styles.free} onSubmit={submitFree}>
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            value={text}
            maxLength={ASK_TITLE_MAX}
            placeholder={ASK_FREE_TEXT}
            aria-label={`Your question for ${shown}`}
            enterKeyHint="send"
            onChange={e => {
              setText(e.target.value)
              setError(line => (line === TOO_SHORT_LINE ? null : line))
            }}
            onKeyDown={e => {
              if (e.key === 'Escape' && !text) {
                setOpenNote(null)
              }
            }}
          />
          <button type="submit" className={styles.submit} disabled={!text.trim() || busy !== null}>
            {'Ask'}
          </button>
        </form>
      )}
      {done === 'signed_out' && (
        <p className={styles.toast} role="status">
          {'Asked. '}
          <button type="button" className={styles.toastAction} onClick={offerSignIn}>
            {'Sign in'}
          </button>
          {' to get the answer by email.'}
        </p>
      )}
      {done === 'claimed' && (
        <p className={styles.sent} role="status">
          {askClaimedLine(shown)}
        </p>
      )}
      {done === 'signed_in' && (
        <p className={styles.sent} role="status">
          {askSentLine(shown, product.name)}
        </p>
      )}
      {error && (
        <p className={styles.error} role="status">
          {error}
        </p>
      )}
      {/* An interactive bot check (a first signed-out ask) shows here, under the chips. */}
      <div ref={turnstile.containerRef} className={styles.check} />
    </div>
  )

  return <>{children({ button, panel })}</>
}

export default AskOwner
